import React, { useState } from "react";
import JumboPayModal from "../components/Reusable/JumboPayModal/JumboPayModal.js";
import JubmoPayModalContainer from "../components/Reusable/JumboPayModal/JubmoPayModalContainer.js";
import queryString from "query-string";
import parse from "html-react-parser";
import ToastHtmlComponent from "components/Reusable/ToastHtmlComponent/ToastHtmlComponent.js";
import SimpleContent from "components/Structure/ModalManager/SimpleContent.js";
import config from "config";
import checkCurrDomainIsOrgDomain from "helpers/checkCurrDomainIsOrgDomain.ts";
import { useContent } from "../contexts/contentContext/contentContext.js";
import { useMail } from "../contexts/mailContext/mailContext.js";
import { useMailView } from "../contexts/mailViewContext/mailViewContext.js";
import { useModal } from "../contexts/modalContext/modalContext.js";
import { useToasts } from "react-toast-notifications/dist/ToastProvider.js";
import { useUser } from "../contexts/userContext/userContext.js";
import { useLocation } from "react-router-dom";
import { useRecaptcha } from "contexts/recaptchaContext/recaptchaContext.js";

export const useDownloadRequest = ({
  itemId,
  mailId,
  folderId,
  userId,
  inboxFilesList,
}) => {
  const {
    startDownload,
    getPaymentData,
    getMailId,
    isDownloadAllowed,
    getMailSecurity,
    getMailMetaData,
  } = useMail();

  const { isLoggedIn } = useUser();

  const { getFilesSelection } = useMailView();

  const { addToast } = useToasts();

  const { getTextByKey, getPath, getRecaptchaRequired } = useContent();

  const { setModal } = useModal();

  const { getLanguage } = useContent();

  const { setShowRecaptcha } = useRecaptcha();

  const isCustomDomain = checkCurrDomainIsOrgDomain();

  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const location = useLocation();
  const searchParams = queryString.parse(location.search);
  const handleDownload = async (recaptchaResponse) => {
    setIsClicked(true);

    // logic for dynamic captcha, if recaptcha is required, show it and wait for response with recaptchaResponse
    if (
      (!recaptchaResponse || typeof recaptchaResponse !== "string") &&
      getRecaptchaRequired()
    ) {
      setShowRecaptcha("global", handleDownload);
      setIsClicked(false);
      return;
    }
    // clear the callback from the recaptcha context
    setShowRecaptcha("global", false);
    if (isWaitingForResponse) return;
    if (itemId === null && window.scrollY < 200) {
      document.querySelector(".scroll-to-bottom-button").click();
    }
    if (
      getMailSecurity()?.downloadLimit === getMailMetaData().DownloadsCount &&
      getMailSecurity()?.downloadLimit > 0
    ) {
      addToast(getTextByKey(`download-limit-reached`), {
        appearance: "warning",
        autoDismissTimeout: 10000,
      });
      return;
    }
    if (!isDownloadAllowed() && !mailId) {
      setModal({
        modal: () => (
          <JumboPayModal
            paymentData={getPaymentData()}
            getMailId={getMailId()}
          />
        ),
        container: JubmoPayModalContainer,
      });
    } else {
      setIsWaitingForResponse(true);

      const availableResponse = !isCustomDomain
        ? Promise.all([
            fetch(
              "https://download.jumbomail.me/apicheck.txt?Expires=2145916800&Signature=EZRN7Y2ID96l~~DbZEPSszNfwUwG6YXCi7n9PSoLb7hFKzxJnFUKUXwG5cuhiP8OVTBqXb~sbZ3cbBn5fivxO8EKSDrz5AXoVhUUSRllqU0AQEFtLl1atkMJAl84E6wJs0lM7Yg9PpOpmj7bvo~EC8kzJrrG9Go03NW2FFpzXr3VXmN~1TkRcxXxHVZj9XhEEBsyw1ta9P2151stwhd9FZ5J1IZDmLFHOFcMYdIZ-esSAFcfUwBSeDhqdVR9KkDkIP57AAoM-UIixjrFFj3q9f-jhFTGMEZOT39BOAX1cKuUBLWJmqhiWLkOTqxDfN3lpJStVzs5RFPmqIj3iXG1pA__&Key-Pair-Id=APKAIUDTSDADOHYIWD6Q"
            ).catch((err) => {
              return { status: null };
            }),
          ])
        : [];

      const response =
        isCustomDomain ||
        (await availableResponse).some((response) => response?.status === 200)
          ? await startDownload({
              fileId: itemId,
              folderId: folderId,
              selectedFiles: inboxFilesList || getFilesSelection(),
              isProxySuccess: true,
              culture: getLanguage(),
              mailId: mailId || getMailId(),
              uid: userId || (isLoggedIn() ? null : searchParams?.uid),
              recaptchaResponse,
            })
          : {
              status: "failed-connection",
            };

      if (response?.status === "failed-connection") {
        const link = `${config.baseurl}/${getPath()}support/a/internet-block`;
        const messagePlainText = getTextByKey(
          "network-connection-issue-text"
        ).replace("#link#", link);

        setModal({
          modal: SimpleContent,
          props: {
            image: {
              name: "robot",
              ext: "png",
            },
            title: "network-connection-issue-title",
            messageText: parse(messagePlainText),
          },
        });

        setIsWaitingForResponse(false);

        return;
      }

      if (response?.status !== "success") {
        if (response.data === "passed-limit-download") {
          let errorMessage = getTextByKey(`passed-limit-download`);

          addToast(<ToastHtmlComponent htmlStr={errorMessage} />, {
            appearance: "error",
            autoDismissTimeout: 10000,
          });
        } else if (response.data === "CaptchaRequired") {
          setShowRecaptcha("global", handleDownload);
          setIsClicked(false);
          return;
        } else {
          addToast(getTextByKey(`please-contact-support`), {
            appearance: "warning",
            autoDismissTimeout: 10000,
          });
        }
      }

      setTimeout(() => {
        setIsWaitingForResponse(false);
      }, 2000);
    }
  };

  return { handleDownload, isWaitingForResponse, isClicked };
};
